import { useEffect, useRef, useState } from "react";
import '../print-manager/print-manager.scss';

export function PrintManagerConfig() {
    const [customUrl, setCustomUrl] = useState(
        localStorage.getItem("print-manager-url") || "https://localhost:7036"
    );
    const [printers, setPrinters] = useState<string[]>([]);
    const [selectedPrinter, setSelectedPrinter] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [apiHealthy, setApiHealthy] = useState<boolean | null>(null);
    const [printerTestError, setPrinterTestError] = useState<string | null>(null);

    // Ref para fullscreen
    const containerRef = useRef<HTMLDivElement>(null);

    function handleEnterFullscreen() {
        const el = containerRef.current;
        if (el && el.requestFullscreen) {
            el.requestFullscreen();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (el && (el as any).webkitRequestFullscreen) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (el as any).webkitRequestFullscreen();
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (el && (el as any).msRequestFullscreen) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (el as any).msRequestFullscreen();
        }
    }

    function handleSaveUrl() {
        localStorage.setItem("print-manager-url", customUrl);
        window.location.reload();
    }

    function handleSelectPrinter(printer: string) {
        setSelectedPrinter(printer);
        localStorage.setItem("print-manager-printer", printer);
    }

    function openPrintersUrl() {
        window.open(`${customUrl}/printers`);
    }

    useEffect(() => {
        const root = document.getElementById('root');
        if (root) root.classList.add('print-manager-config-active');
        return () => {
            if (root) root.classList.remove('print-manager-config-active');
        };
    }, []);

    useEffect(() => {
        const savedPrinter = localStorage.getItem("print-manager-printer");
        if (savedPrinter) setSelectedPrinter(savedPrinter);

        setLoading(true);
        setError(null);
        setApiHealthy(null);
        setPrinterTestError(null);

        fetch(`${customUrl}/health`, { method: "GET" })
            .then((res) => {
                if (!res.ok) throw new Error();
                setApiHealthy(true);
            })
            .catch(() => {
                setApiHealthy(false);
                setError("Não foi possível conectar ao serviço de impressão. Verifique se o endereço informado está correto e atualize-o no campo acima, se necessário.");
                setPrinterTestError(null);
                return;
            })
            .finally(() => setLoading(false));

        const myHeaders = new Headers();
        myHeaders.append("accept", "*/*");

        fetch(`${customUrl}/printers`, {
            method: "GET",
            headers: myHeaders,
            redirect: "follow"
        })
            .then((response) => response.json())
            .then((result) => {
                if (result.success && Array.isArray(result.printers)) {
                    setPrinters(result.printers);
                } else {
                    setPrinterTestError("Não foi possível carregar as impressoras automaticamente.");
                }
            })
            .catch(() => setPrinterTestError("Não foi possível carregar as impressoras automaticamente."))
            .finally(() => setLoading(false));
    }, [customUrl]);

    return (
        <div
            style={{
                minHeight: "100vh",
                minWidth: "100vw",
                background: "#111",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "Inter, Arial, sans-serif",
                overflow: "auto",
            }}
            id="print-manager-config-page"
        >
            <div
                ref={containerRef}
                style={{
                    background: "#fff",
                    borderRadius: 24,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    padding: "48px 32px",
                    minWidth: 340,
                    maxWidth: 400,
                    width: "100%",
                    display: "grid",
                    gap: 32,
                    justifyItems: "center",
                    margin: "48px 0",
                }}
            >
                <img
                    src="/logo.svg"
                    alt="Logo"
                    style={{ width: 80, marginBottom: 16, cursor: "pointer" }}
                    onClick={handleEnterFullscreen}
                />
                <img
                    src="/embaixador-moto-esquerda.svg"
                    alt="Moto"
                    style={{ width: 120, opacity: 0.85, cursor: "pointer" }}
                    onClick={handleEnterFullscreen}
                />
                <h2 style={{ margin: 0, fontWeight: 700, fontSize: 22, color: "#222" }}>
                    Configurar Impressora
                </h2>
                <p style={{ color: "#555", textAlign: "center", margin: 0 }}>
                    Selecione a impressora padrão para impressão de senhas.
                </p>

                {/* Campo para host/IP e porta local */}
                <div style={{ width: "100%", margin: "12px 0" }}>
                    <label style={{ fontWeight: 600, color: "#222", fontSize: 15 }}>
                        Endereço do serviço de impressão (local):
                    </label>
                    <input
                        type="text"
                        value={customUrl}
                        onChange={e => setCustomUrl(e.target.value)}
                        style={{
                            width: "100%",
                            padding: "10px",
                            borderRadius: 8,
                            border: "1px solid #ddd",
                            fontSize: 15,
                            marginTop: 6,
                            marginBottom: 10,
                            background: "#f8fafc",
                        }}
                        placeholder="Ex: http://localhost:7036"
                    />
                    <button
                        onClick={handleSaveUrl}
                        style={{
                            background: "#e14231",
                            color: "#fff",
                            border: "none",
                            borderRadius: 8,
                            padding: "10px 18px",
                            fontWeight: 700,
                            fontSize: 15,
                            cursor: "pointer",
                            marginBottom: 10,
                        }}
                    >
                        Salvar endereço local
                    </button>
                </div>

                {/* Mostra erro se a API não está saudável */}
                {apiHealthy === false && (
                    <div style={{ color: "#e14231", marginBottom: 12, textAlign: "center" }}>
                        {error}
                             <div style={{ color: "#e14231", marginTop: 8 }}>
                                    <br />
                                    <button
                                        onClick={openPrintersUrl}
                                        style={{
                                            marginTop: 8,
                                            background: "#fff",
                                            color: "#e14231",
                                            border: "1px solid #e14231",
                                            borderRadius: 8,
                                            padding: "6px 14px",
                                            fontWeight: 700,
                                            fontSize: 15,
                                            cursor: "pointer",
                                        }}
                                    >
                                        Testar manualmente
                                    </button>
                                </div>
                    </div>
                )}

                {/* Loading */}
                {loading && apiHealthy !== false && <div>Carregando impressoras...</div>}

                {/* Só mostra lista de impressoras se a API está saudável */}
                {!loading && apiHealthy && !error && (
                    <div style={{ width: "100%", margin: "12px 0" }}>
                        <select
                            value={selectedPrinter || ""}
                            onChange={e => handleSelectPrinter(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "12px",
                                borderRadius: 8,
                                border: "1px solid #ddd",
                                fontSize: 16,
                                background: "#f8fafc",
                                marginBottom: 16,
                            }}
                        >
                            <option value="" disabled>
                                Selecione uma impressora
                            </option>
                            {printers.map((printer) => (
                                <option key={printer} value={printer}>
                                    {printer}
                                </option>
                            ))}
                        </select>
                        {selectedPrinter && (
                            <div style={{ color: "#222", fontSize: 15, marginTop: 8 }}>
                                Impressora padrão: <strong>{selectedPrinter}</strong>
                            </div>
                        )}

                        {/* Testar conexão com impressoras */}
                        <div style={{ marginTop: 24 }}>
                            {printerTestError && (
                                <div style={{ color: "#e14231", marginTop: 8 }}>
                                    {printerTestError}
                                    <br />
                                    <button
                                        onClick={openPrintersUrl}
                                        style={{
                                            marginTop: 8,
                                            background: "#fff",
                                            color: "#e14231",
                                            border: "1px solid #e14231",
                                            borderRadius: 8,
                                            padding: "6px 14px",
                                            fontWeight: 700,
                                            fontSize: 15,
                                            cursor: "pointer",
                                        }}
                                    >
                                        Abrir /printers manualmente
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}